import { FC } from 'react';
import { Plus } from 'lucide-react';

import type { PlanCourse, RequirementGroup } from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';

import { RequirementGroupCard } from '../RequirementGroupCard/RequirementGroupCard';
import { CourseRow } from '../CourseRow/CourseRow';
import { groupAnchorId, rowKey, sumCredits } from '../../lib/planView';

interface RequirementSectionProps {
  group: RequirementGroup;
  title?: string;
  emptyText?: string;
  editable?: boolean;
  showBadges?: boolean;
  showProgress?: boolean;
  onSwitch?: (group: RequirementGroup, course: PlanCourse) => void;
  onRemove?: (group: RequirementGroup, course: PlanCourse) => void;
  onAdd?: (group: RequirementGroup) => void;
}

export const RequirementSection: FC<RequirementSectionProps> = ({
  group,
  title,
  emptyText = 'No courses yet.',
  editable = false,
  showBadges = true,
  showProgress = true,
  onSwitch,
  onRemove,
  onAdd,
}) => {
  const heading = title ?? group.title;
  const subtitle = `${group.courses.length} courses · ${group.requiredCredits} credits`;
  const satisfied = sumCredits(group.courses);
  const recommended = group.kind !== 'required';

  const handleSwitch = onSwitch ? (course: PlanCourse) => onSwitch(group, course) : undefined;
  const handleRemove = onRemove ? (course: PlanCourse) => onRemove(group, course) : undefined;
  const handleAdd = () => onAdd?.(group);

  const emptyLine =
    group.courses.length === 0 ? (
      <p className="[font-size:var(--fs-l5)]! m-0! text-(--neutral-black-NB600)!">{emptyText}</p>
    ) : null;

  const courseRows = group.courses.map((course) => (
    <CourseRow
      key={rowKey(course)}
      course={course}
      recommended={recommended}
      showBadges={showBadges}
      onSwitch={handleSwitch}
      onRemove={handleRemove}
    />
  ));

  const addButton =
    editable && onAdd ? (
      <div className="pt-1">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="size-4" />}
          text="Add course"
          onClick={handleAdd}
        />
      </div>
    ) : null;

  return (
    <RequirementGroupCard
      id={groupAnchorId(group.id)}
      title={heading}
      subtitle={subtitle}
      satisfiedCredits={satisfied}
      requiredCredits={group.requiredCredits}
      showProgress={showProgress}
    >
      {emptyLine}
      {courseRows}
      {addButton}
    </RequirementGroupCard>
  );
};
